/**
 * Pure polygon helpers in percent space (§3.2 `geo/polygon.ts`).
 *
 * Winding convention (§4.0): the outer ring is **clockwise in screen space**
 * (y down), holes counter-clockwise. With the shoelace formula in a y-down
 * frame a clockwise ring has a *positive* signed area, so `normalizeWinding`
 * makes outer rings positive and holes negative.
 */
import { bboxOfPoly, bboxOfRing, dist, midpoint, type Bbox, type Poly, type Pt, type Ring, type Seg } from './types'

/** Signed shoelace area; positive = clockwise on screen (y down). */
export function signedArea(ring: Ring): number {
  let s = 0
  const n = ring.length
  if (n < 3) return 0
  for (let i = 0; i < n; i++) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    s += a[0] * b[1] - b[0] * a[1]
  }
  return s / 2
}

/** Area of the outer ring minus its holes, in percent² (never negative). */
export function area(poly: Poly): number {
  let a = Math.abs(signedArea(poly.outer))
  for (const h of poly.holes ?? []) a -= Math.abs(signedArea(h))
  return Math.max(0, a)
}

export function isClockwise(ring: Ring): boolean {
  return signedArea(ring) > 0
}

/** Outer ring clockwise, holes counter-clockwise (§4.0). Returns a new poly; rings that are already right are shared. */
export function normalizeWinding(poly: Poly): Poly {
  const outer = isClockwise(poly.outer) || signedArea(poly.outer) === 0 ? poly.outer : [...poly.outer].reverse()
  if (!poly.holes || poly.holes.length === 0) return outer === poly.outer ? poly : { outer }
  const holes = poly.holes.map((h) => (isClockwise(h) ? [...h].reverse() : h))
  return { outer, holes }
}

/** Ray-casting test against a single ring (boundary counts as inside). */
export function pointInRing(p: Pt, ring: Ring): boolean {
  const n = ring.length
  if (n < 3) return false
  const [x, y] = p
  let inside = false
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ring[i]!
    const b = ring[j]!
    if (distToSegment(p, a, b) < 1e-9) return true
    const intersects = a[1] > y !== b[1] > y && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1] || 1e-12) + a[0]
    if (intersects) inside = !inside
  }
  return inside
}

/** Inside the outer ring and outside every hole. */
export function pointInPolygon(p: Pt, poly: Poly): boolean {
  if (!pointInRing(p, poly.outer)) return false
  for (const h of poly.holes ?? []) if (pointInRing(p, h)) return false
  return true
}

/** Area-weighted centroid of the outer ring (falls back to the vertex mean for degenerate rings). */
export function centroid(ring: Ring): Pt {
  const n = ring.length
  if (n === 0) return [0, 0]
  const a = signedArea(ring)
  if (Math.abs(a) < 1e-9) {
    let sx = 0
    let sy = 0
    for (const [x, y] of ring) {
      sx += x
      sy += y
    }
    return [sx / n, sy / n]
  }
  let cx = 0
  let cy = 0
  for (let i = 0; i < n; i++) {
    const p = ring[i]!
    const q = ring[(i + 1) % n]!
    const f = p[0] * q[1] - q[0] * p[1]
    cx += (p[0] + q[0]) * f
    cy += (p[1] + q[1]) * f
  }
  return [cx / (6 * a), cy / (6 * a)]
}

/** Signed distance from `p` to the polygon boundary: positive inside, negative outside. */
export function signedDistanceToBoundary(p: Pt, poly: Poly): number {
  let best = Infinity
  const rings = [poly.outer, ...(poly.holes ?? [])]
  for (const ring of rings) {
    const n = ring.length
    for (let i = 0, j = n - 1; i < n; j = i++) {
      best = Math.min(best, distToSegment(p, ring[j]!, ring[i]!))
    }
  }
  return pointInPolygon(p, poly) ? best : -best
}

/**
 * Pole of inaccessibility (the point deepest inside the polygon), a compact
 * port of the polylabel grid search. Used to place labels and kind icons
 * where they never sit on an edge. `precision` is in percent.
 */
export function poleOfInaccessibility(poly: Poly, precision = 0.5): Pt {
  if (poly.outer.length < 3) return centroid(poly.outer)
  const bb = bboxOfRing(poly.outer)
  const w = bb.x1 - bb.x0
  const h = bb.y1 - bb.y0
  const cellSize = Math.min(w, h)
  if (cellSize === 0) return [bb.x0, bb.y0]

  interface Cell {
    x: number
    y: number
    h: number
    d: number
    max: number
  }
  const mk = (x: number, y: number, half: number): Cell => {
    const d = signedDistanceToBoundary([x, y], poly)
    return { x, y, h: half, d, max: d + half * Math.SQRT2 }
  }

  const queue: Cell[] = []
  let half = cellSize / 2
  for (let x = bb.x0; x < bb.x1; x += cellSize) for (let y = bb.y0; y < bb.y1; y += cellSize) queue.push(mk(x + half, y + half, half))

  const c = centroid(poly.outer)
  let best = mk(c[0], c[1], 0)
  const bboxCell = mk(bb.x0 + w / 2, bb.y0 + h / 2, 0)
  if (bboxCell.d > best.d) best = bboxCell

  while (queue.length) {
    queue.sort((a, b) => b.max - a.max)
    const cell = queue.shift()!
    if (cell.d > best.d) best = cell
    if (cell.max - best.d <= precision) continue
    half = cell.h / 2
    queue.push(mk(cell.x - half, cell.y - half, half), mk(cell.x + half, cell.y - half, half), mk(cell.x - half, cell.y + half, half), mk(cell.x + half, cell.y + half, half))
  }
  return [best.x, best.y]
}

/** Distance from `p` to the segment `ab`. */
export function distToSegment(p: Pt, a: Pt, b: Pt): number {
  return dist(p, closestPointOnSegment(p, a, b))
}

export function closestPointOnSegment(p: Pt, a: Pt, b: Pt): Pt {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const l2 = dx * dx + dy * dy
  if (l2 === 0) return a
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2
  t = Math.max(0, Math.min(1, t))
  return [a[0] + t * dx, a[1] + t * dy]
}

function orient(a: Pt, b: Pt, c: Pt): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
}

function onSegment(a: Pt, b: Pt, c: Pt): boolean {
  return Math.min(a[0], b[0]) - 1e-9 <= c[0] && c[0] <= Math.max(a[0], b[0]) + 1e-9 && Math.min(a[1], b[1]) - 1e-9 <= c[1] && c[1] <= Math.max(a[1], b[1]) + 1e-9
}

/** Proper or touching intersection of segments `ab` and `cd`. */
export function segmentsIntersect(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c)
  const o2 = orient(a, b, d)
  const o3 = orient(c, d, a)
  const o4 = orient(c, d, b)
  if (o1 * o2 < 0 && o3 * o4 < 0) return true
  if (Math.abs(o1) < 1e-12 && onSegment(a, b, c)) return true
  if (Math.abs(o2) < 1e-12 && onSegment(a, b, d)) return true
  if (Math.abs(o3) < 1e-12 && onSegment(c, d, a)) return true
  if (Math.abs(o4) < 1e-12 && onSegment(c, d, b)) return true
  return false
}

/**
 * True when the ring has ≥ 3 vertices, non-zero area and no two
 * non-adjacent edges cross (`E_RING_DEGENERATE` otherwise). O(n²), which is
 * fine for hand-drawn rings (tens of vertices).
 */
export function isSimple(ring: Ring): boolean {
  const n = ring.length
  if (n < 3) return false
  if (Math.abs(signedArea(ring)) < 1e-6) return false
  for (let i = 0; i < n; i++) {
    const a = ring[i]!
    const b = ring[(i + 1) % n]!
    for (let j = i + 1; j < n; j++) {
      // Skip the edge itself and the two adjacent edges (they share a vertex).
      if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue
      const c = ring[j]!
      const d = ring[(j + 1) % n]!
      if (segmentsIntersect(a, b, c, d)) return false
    }
  }
  return true
}

/** Clip a value to `[0, 100]`. */
export const clampPct = (v: number): number => (v < 0 ? 0 : v > 100 ? 100 : v)
export const clampPt = (p: Pt): Pt => [clampPct(p[0]), clampPct(p[1])]
export const clampRing = (r: Ring): Ring => r.map(clampPt)
export const clampPoly = (p: Poly): Poly => (p.holes ? { outer: clampRing(p.outer), holes: p.holes.map(clampRing) } : { outer: clampRing(p.outer) })

/** Round to the contract's 2 decimals. */
export const round2 = (v: number): number => Math.round((v + Number.EPSILON) * 100) / 100
export const roundPt2 = (p: Pt): Pt => [round2(p[0]), round2(p[1])]
export const roundRing2 = (r: Ring): Ring => r.map(roundPt2)

/** Drop consecutive duplicate vertices (and a trailing vertex equal to the first). */
export function dedupeRing(ring: Ring, eps = 1e-6): Ring {
  const out: Ring = []
  for (const p of ring) {
    const last = out[out.length - 1]
    if (last && Math.abs(last[0] - p[0]) <= eps && Math.abs(last[1] - p[1]) <= eps) continue
    out.push(p)
  }
  const first = out[0]
  const last = out[out.length - 1]
  if (out.length > 1 && first && last && Math.abs(first[0] - last[0]) <= eps && Math.abs(first[1] - last[1]) <= eps) out.pop()
  return out
}

/** Axis-aligned rectangle ring from two corners, clockwise in screen space. */
export function rectRing(a: Pt, b: Pt): Ring {
  const x0 = Math.min(a[0], b[0])
  const x1 = Math.max(a[0], b[0])
  const y0 = Math.min(a[1], b[1])
  const y1 = Math.max(a[1], b[1])
  return [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ]
}

/** Full-width horizontal band between two y values. */
export function bandRing(y0: number, y1: number): Ring {
  return rectRing([0, Math.min(y0, y1)], [100, Math.max(y0, y1)])
}

/** Move every vertex of a polygon by `delta`, clipped to the frame. */
export function translatePoly(poly: Poly, delta: Pt): Poly {
  const move = (r: Ring): Ring => r.map((p) => clampPt([p[0] + delta[0], p[1] + delta[1]]))
  return poly.holes ? { outer: move(poly.outer), holes: poly.holes.map(move) } : { outer: move(poly.outer) }
}

/** Edge midpoints of a ring, in edge order (edge i joins vertex i and i+1). */
export function edgeMidpoints(ring: Ring): Pt[] {
  const n = ring.length
  const out: Pt[] = []
  for (let i = 0; i < n; i++) out.push(midpoint(ring[i]!, ring[(i + 1) % n]!))
  return out
}

/** Index of the vertex nearest to `p` within `tol`, or -1. */
export function nearestVertex(ring: Ring, p: Pt, tol: number): number {
  let best = -1
  let bestD = tol
  ring.forEach((v, i) => {
    const d = dist(v, p)
    if (d <= bestD) {
      bestD = d
      best = i
    }
  })
  return best
}

/** Index of the edge nearest to `p` within `tol`, or -1. */
export function nearestEdge(ring: Ring, p: Pt, tol: number): number {
  let best = -1
  let bestD = tol
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const d = distToSegment(p, ring[i]!, ring[(i + 1) % n]!)
    if (d <= bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

/* Re-exports so a caller that only needs polygon maths has one import. */
export { bboxOfRing, bboxOfPoly, dist, midpoint }
export type { Bbox, Poly, Pt, Ring, Seg }
